import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, CalendarBlank, ArrowRight } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/blog`);
        setPosts(response.data);
      } catch {
        // Silently fail - empty state will be shown
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
     
  }, []);

  const categories = [
    'Direito Trabalhista',
    'Plano de Saúde',
    'BPC/LOAS',
    'Busca e Apreensão',
    'Previdenciário',
    'Dúvidas frequentes',
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-white" data-testid="blog-hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2A2A2A] playfair tracking-tight mb-6">
              Conteúdo Jurídico
            </h1>
            <p className="text-xl text-[#737373] leading-relaxed">
              Informação clara, útil e responsável sobre Direito Trabalhista, Plano de Saúde, BPC/LOAS e Defesa em Busca e Apreensão.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Institutional Block */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="blog-intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold text-[#2A2A2A] playfair mb-6">
              Conteúdo com clareza e responsabilidade
            </h2>
            <p className="text-lg text-[#737373] leading-relaxed">
              A RDC Advogados reúne nesta seção conteúdos informativos voltados às dúvidas mais recorrentes nas
              áreas em que atua. O objetivo é oferecer informação jurídica acessível, bem organizada e tecnicamente
              responsável, ajudando o leitor a compreender melhor sua situação antes de tomar decisões.
            </p>
          </motion.div>

          {/* Categories */}
          <motion.div {...fadeUp} className="mb-16">
            <h3 className="text-2xl font-bold text-[#2A2A2A] playfair mb-6 text-center">Categorias</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category, index) => (
                <button
                  key={category}
                  className="bg-white border border-[#2A2A2A]/10 rounded-sm px-6 py-3 text-[#2A2A2A] hover:bg-[#2A2A2A] hover:text-white transition-colors duration-200"
                  data-testid={`category-${index}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Blog Posts */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#737373] text-lg">Carregando artigos...</p>
            </div>
          ) : posts.length === 0 ? (
            <motion.div {...fadeUp} className="text-center py-12">
              <BookOpen size={64} weight="duotone" className="text-[#A98C67] mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-[#2A2A2A] playfair mb-4">
                Em breve, novos conteúdos
              </h3>
              <p className="text-lg text-[#737373] max-w-2xl mx-auto leading-relaxed">
                Estamos preparando conteúdos informativos de qualidade para ajudá-lo a compreender melhor questões
                jurídicas relacionadas a Direito Trabalhista, Plano de Saúde, BPC/LOAS e Defesa em Busca e Apreensão.
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  {...fadeUp}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="bg-white border border-[#2A2A2A]/10 rounded-sm p-6 hover:-translate-y-1 transition-transform duration-300 shadow-sm"
                  data-testid={`blog-post-${index}`}
                >
                  <div className="mb-4">
                    <span className="inline-block bg-[#2A2A2A]/5 text-[#2A2A2A] text-sm font-medium px-3 py-1 rounded-sm">
                      {post.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#2A2A2A] playfair mb-3">{post.title}</h3>
                  <p className="text-[#737373] leading-relaxed mb-4">{post.excerpt}</p>
                  <div className="flex items-center text-sm text-[#999999] mb-4">
                    <CalendarBlank size={16} weight="duotone" className="mr-2" />
                    <span>{new Date(post.created_at).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <button className="text-[#2A2A2A] font-medium flex items-center hover:text-[#A98C67] transition-colors group">
                    Ler mais
                    <ArrowRight
                      size={18}
                      weight="bold"
                      className="ml-2 group-hover:translate-x-1 transition-transform"
                    />
                  </button>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-24 bg-white" data-testid="blog-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-[#2A2A2A] playfair mb-6">
              Informação jurídica bem construída também é uma forma de cuidado
            </h2>
            <p className="text-lg text-[#737373] leading-relaxed">
              Ao disponibilizar conteúdo informativo, a RDC Advogados busca ampliar o acesso à informação jurídica
              de maneira séria, objetiva e responsável. O conteúdo publicado não substitui a análise individual de
              cada caso, mas oferece uma base segura para que o leitor compreenda melhor os temas que impactam a
              sua realidade.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="blog-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold playfair mb-6">
              Precisa de orientação sobre um caso concreto?
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Os conteúdos do site têm caráter informativo. Para análise individual da sua situação, entre em
              contato com a RDC Advogados.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contato">
                <button
                  data-testid="blog-contact-button"
                  className="bg-white text-[#2A2A2A] hover:bg-white/90 rounded-sm transition-colors duration-200 font-medium px-8 py-3 text-base"
                >
                  Solicitar atendimento
                </button>
              </a>
              <a href="https://wa.me/552128880258" target="_blank" rel="noopener noreferrer">
                <button
                  data-testid="blog-whatsapp-button"
                  className="bg-[#25D366] text-white hover:bg-[#20BA5A] border-0 rounded-sm transition-colors duration-200 font-medium px-8 py-3 text-base"
                >
                  Falar pelo WhatsApp
                </button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog;